import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Promotions.module.css';
import StatCard from '../../components/StatCard/StatCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { api, mapPromotion } from '../../lib/api';

const MAX_TITLE = 80;
const MAX_BODY = 240;

const AUDIENCES = [
    { value: 'all', label: 'Everyone', description: 'Every active account on the platform' },
    { value: 'buyers', label: 'Buyers', description: 'Anyone browsing and ordering — includes buyer + seller accounts' },
    { value: 'sellers', label: 'Sellers', description: 'Anyone with listings — includes buyer + seller accounts' },
];

const EMPTY_FORM = { title: '', body: '', audience: 'all', link: '' };

// What tapping the notification opens in the app. Must match the API's
// PROMOTION_LINKS; an app build that doesn't know a link just opens the app.
const LINKS = [
    { value: '', label: 'Nothing — just open the app' },
    { value: 'affiliate', label: 'Affiliate programme page' },
];

const Promotions = () => {
    const [promotions, setPromotions] = useState([]);
    const [counts, setCounts] = useState({ all: 0, buyers: 0, sellers: 0 });
    const [form, setForm] = useState(EMPTY_FORM);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(async () => {
        try {
            const { promotions: list, audienceCounts } = await api('/admin/promotions');
            setPromotions(list.map(mapPromotion));
            setCounts(audienceCounts);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // The API answers as soon as a send is accepted and fans it out behind the
    // request, so the counts on a fresh row climb for a few seconds after it
    // appears. Poll only while something is still going out, and stop as soon as
    // nothing is — an idle page must not sit there refetching forever.
    const sendingCount = promotions.filter((promotion) => promotion.status === 'Sending').length;
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!sendingCount) return undefined;
        const timer = setInterval(() => loadRef.current(), 3000);
        return () => clearInterval(timer);
    }, [sendingCount]);

    const audience = AUDIENCES.find((option) => option.value === form.audience);
    const reach = counts[form.audience] ?? 0;
    const formValid = form.title.trim() && form.body.trim() && reach > 0;

    const handleSend = async () => {
        setConfirmOpen(false);
        setSending(true);
        setError('');
        try {
            await api('/admin/promotions', {
                method: 'POST',
                body: {
                    title: form.title.trim(),
                    body: form.body.trim(),
                    audience: form.audience,
                    link: form.link,
                },
            });
            setForm(EMPTY_FORM);
            await load();
        } catch (err) {
            setError(err.message);
        }
        setSending(false);
    };

    return (
        <div>
            <div className="row g-3">
                {AUDIENCES.map((option) => (
                    <div className="col-12 col-md-4" key={option.value}>
                        <StatCard value={counts[option.value]?.toLocaleString() ?? '0'} label={`${option.label} reachable`} />
                    </div>
                ))}
            </div>

            <div className="row g-3 mt-1">
                <div className="col-12 col-lg-7">
                    <div className={`${styles.card} p-4 h-100`}>
                        <h2 className={`${styles.cardTitle} mb-0`}>Send a promotion</h2>
                        <p className={`${styles.cardSubtitle} mt-1 mb-0`}>
                            Lands in the app's notifications feed and as a push on every device the
                            audience has signed in on.
                        </p>

                        <label htmlFor="promo-audience" className={`${styles.inputLabel} mt-4 mb-2`}>Audience</label>
                        <select
                            id="promo-audience"
                            className={`${styles.input} w-100`}
                            value={form.audience}
                            onChange={(event) => setForm({ ...form, audience: event.target.value })}
                        >
                            {AUDIENCES.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label} ({(counts[option.value] ?? 0).toLocaleString()})
                                </option>
                            ))}
                        </select>
                        <p className={`${styles.helpText} mt-2 mb-0`}>{audience?.description}</p>

                        <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                            <label htmlFor="promo-title" className={styles.inputLabel}>Title</label>
                            <span className={styles.counter}>{form.title.length}/{MAX_TITLE}</span>
                        </div>
                        <input
                            id="promo-title"
                            type="text"
                            maxLength={MAX_TITLE}
                            className={`${styles.input} w-100`}
                            placeholder="E.g. Double points this weekend"
                            value={form.title}
                            onChange={(event) => setForm({ ...form, title: event.target.value })}
                        />

                        <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                            <label htmlFor="promo-body" className={styles.inputLabel}>Message</label>
                            <span className={styles.counter}>{form.body.length}/{MAX_BODY}</span>
                        </div>
                        <textarea
                            id="promo-body"
                            rows={4}
                            maxLength={MAX_BODY}
                            className={`${styles.input} ${styles.textarea} w-100`}
                            placeholder="E.g. Buy any points package before Sunday and get 25% extra points."
                            value={form.body}
                            onChange={(event) => setForm({ ...form, body: event.target.value })}
                        />

                        <label htmlFor="promo-link" className={`${styles.inputLabel} mt-4 mb-2`}>When tapped, open</label>
                        <select
                            id="promo-link"
                            className={`${styles.input} w-100`}
                            value={form.link}
                            onChange={(event) => setForm({ ...form, link: event.target.value })}
                        >
                            {LINKS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <p className={`${styles.helpText} mt-2 mb-0`}>
                            Works from the push notification and from the app's notification list.
                        </p>

                        {error && <p className={`${styles.error} mt-3 mb-0`}>{error}</p>}

                        <Button
                            className="w-100 mt-4"
                            disabled={!formValid || sending}
                            onClick={() => setConfirmOpen(true)}
                        >
                            {sending ? 'Sending...' : `Send to ${reach.toLocaleString()} ${reach === 1 ? 'user' : 'users'}`}
                        </Button>
                    </div>
                </div>

                <div className="col-12 col-lg-5">
                    <div className={`${styles.card} p-4 h-100 d-flex flex-column`}>
                        <h2 className={`${styles.cardTitle} mb-0`}>Preview</h2>
                        <p className={`${styles.cardSubtitle} mt-1 mb-0`}>How it arrives on a phone.</p>

                        <div className={`${styles.preview} mt-4 p-3 d-flex gap-3`}>
                            <div className={styles.previewIcon}>AB</div>
                            <div className="flex-grow-1 overflow-hidden">
                                <div className={styles.previewApp}>AgroBridge · now</div>
                                <div className={styles.previewTitle}>
                                    {form.title.trim() || 'Your title appears here'}
                                </div>
                                <div className={styles.previewBody}>
                                    {form.body.trim() || 'Your message appears here.'}
                                </div>
                            </div>
                        </div>

                        <p className={`${styles.helpText} mt-auto pt-4 mb-0`}>
                            A promotion can't be recalled or edited once it is sent — it is already on
                            people's phones. Everyone in the audience receives it, including accounts
                            with no device registered, who see it next time they open the app.
                        </p>
                    </div>
                </div>
            </div>

            <div className={`${styles.card} mt-4 p-4`}>
                <h2 className={`${styles.cardTitle} mb-0`}>Sent promotions</h2>

                <div className={`${styles.tableWrap} table-responsive mt-3`}>
                    <table className={`${styles.table} w-100`}>
                        <thead>
                            <tr>
                                <th>Promotion</th>
                                <th>Audience</th>
                                <th>Delivered</th>
                                <th>Pushed</th>
                                <th>Status</th>
                                <th>Sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {promotions.map((promotion) => (
                                <tr key={promotion.id}>
                                    <td>
                                        <div className={styles.promoTitle}>{promotion.title}</div>
                                        <div className={styles.promoBody}>{promotion.body}</div>
                                    </td>
                                    <td>{promotion.audience}</td>
                                    <td>{promotion.recipients.toLocaleString()}</td>
                                    <td>
                                        {promotion.pushed.toLocaleString()}
                                        {promotion.pushFailed > 0 && (
                                            <span className={styles.pushFailed}> ({promotion.pushFailed} failed)</span>
                                        )}
                                    </td>
                                    <td>
                                        <StatusBadge
                                            status={promotion.status}
                                            variant={promotion.statusVariant}
                                        />
                                    </td>
                                    <td>
                                        <div>{promotion.sentAt}</div>
                                        {promotion.sentBy && (
                                            <div className={styles.promoBody}>by {promotion.sentBy}</div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {loaded && !promotions.length && (
                        <p className={`${styles.empty} mb-0 py-4 text-center`}>
                            No promotions sent yet.
                        </p>
                    )}
                </div>
            </div>

            {confirmOpen && (
                <ConfirmModal
                    title={`Send to ${reach.toLocaleString()} ${reach === 1 ? 'user' : 'users'}?`}
                    message={`"${form.title.trim()}" goes out to ${audience?.label.toLowerCase()} as a push notification straight away. It cannot be recalled or edited afterwards.`}
                    confirmLabel="Send promotion"
                    onConfirm={handleSend}
                    onClose={() => setConfirmOpen(false)}
                />
            )}
        </div>
    );
};

export default Promotions;
